import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'

interface PasswordForm {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

const GRACE_OPTIONS = [0, 5, 8, 10, 15, 20, 30]

const COMMON_TIMEZONES = [
  { value: 'Europe/Moscow', label: 'Москва (UTC+3)' },
  { value: 'Europe/Kaliningrad', label: 'Калининград (UTC+2)' },
  { value: 'Europe/Samara', label: 'Самара (UTC+4)' },
  { value: 'Asia/Yekaterinburg', label: 'Екатеринбург (UTC+5)' },
  { value: 'Asia/Omsk', label: 'Омск (UTC+6)' },
  { value: 'Asia/Krasnoyarsk', label: 'Красноярск (UTC+7)' },
  { value: 'Asia/Irkutsk', label: 'Иркутск (UTC+8)' },
  { value: 'Asia/Yakutsk', label: 'Якутск (UTC+9)' },
  { value: 'Asia/Vladivostok', label: 'Владивосток (UTC+10)' },
  { value: 'Asia/Magadan', label: 'Магадан (UTC+11)' },
  { value: 'Asia/Kamchatka', label: 'Камчатка (UTC+12)' },
]

export default function SettingsPage() {
  const user = useAuthStore((s) => s.user)
  const qc = useQueryClient()
  const [copied, setCopied] = useState(false)
  const [pwSuccess, setPwSuccess] = useState(false)
  const [settingsSaved, setSettingsSaved] = useState(false)

  const { data: settingsData } = useQuery({
    queryKey: ['tenant-settings'],
    queryFn: () => api.get('/tenants/tenant/settings').then((r) => r.data.settings),
    enabled: user?.role === 'owner',
  })

  const updateSettings = useMutation({
    mutationFn: (body: { gracePeriodMinutes?: number; timezone?: string; contactPhone?: string; notifyNonQrOpen?: boolean; notifyNonQrClose?: boolean }) =>
      api.patch('/tenants/tenant/settings', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant-settings'] })
      setSettingsSaved(true)
      setTimeout(() => setSettingsSaved(false), 3000)
    },
  })

  const pwForm = useForm<PasswordForm>()
  const changePassword = useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string }) =>
      api.patch('/users/me/password', data),
    onSuccess: () => {
      pwForm.reset()
      setPwSuccess(true)
      setTimeout(() => setPwSuccess(false), 3000)
    },
  })

  const onPasswordSubmit = pwForm.handleSubmit((data) => {
    if (data.newPassword !== data.confirmPassword) {
      pwForm.setError('confirmPassword', { message: 'Пароли не совпадают' })
      return
    }
    changePassword.mutate({ currentPassword: data.currentPassword, newPassword: data.newPassword })
  })

  const { data: telegramStatus, refetch } = useQuery({
    queryKey: ['telegram', 'status'],
    queryFn: () => api.get('/telegram/status').then((r) => r.data),
  })

  const { data: linkCodeData, mutate: generateCode, isPending } = useMutation({
    mutationFn: () => api.post('/telegram/link-code').then((r) => r.data),
  }) as { data: { code?: string; instruction?: string; expiresAt?: string } | undefined; mutate: () => void; isPending: boolean }

  const unlinkTelegram = useMutation({
    mutationFn: () => api.delete('/telegram/unlink'),
    onSuccess: () => refetch(),
  })

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(`/start ${code}`)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="p-6 max-w-2xl">
      <h2 className="text-xl font-bold text-gray-900 mb-6">Настройки</h2>

      {/* Profile */}
      <section className="bg-white border border-gray-200 rounded-2xl p-5 mb-4">
        <h3 className="font-semibold text-gray-700 mb-3">Профиль</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">Имя</span>
            <span className="font-medium">{user?.name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Телефон</span>
            <span className="font-medium">{user?.phone}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Роль</span>
            <span className="font-medium capitalize">{user?.role}</span>
          </div>
        </div>
      </section>

      {/* Change password */}
      <section className="bg-white border border-gray-200 rounded-2xl p-5 mb-4">
        <h3 className="font-semibold text-gray-700 mb-3">Сменить пароль</h3>
        {pwSuccess && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-3 text-sm text-green-700 font-medium">
            ✅ Пароль успешно изменён
          </div>
        )}
        {changePassword.isError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-3 text-sm text-red-700">
            {(changePassword.error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? 'Ошибка смены пароля'}
          </div>
        )}
        <form onSubmit={onPasswordSubmit} className="space-y-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Текущий пароль</label>
            <input
              type="password"
              autoComplete="current-password"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
              {...pwForm.register('currentPassword', { required: 'Обязательное поле' })}
            />
            {pwForm.formState.errors.currentPassword && (
              <p className="text-xs text-red-500 mt-1">{pwForm.formState.errors.currentPassword.message}</p>
            )}
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Новый пароль</label>
            <input
              type="password"
              autoComplete="new-password"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
              {...pwForm.register('newPassword', { required: 'Обязательное поле', minLength: { value: 6, message: 'Минимум 6 символов' } })}
            />
            {pwForm.formState.errors.newPassword && (
              <p className="text-xs text-red-500 mt-1">{pwForm.formState.errors.newPassword.message}</p>
            )}
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Повторите новый пароль</label>
            <input
              type="password"
              autoComplete="new-password"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
              {...pwForm.register('confirmPassword', { required: 'Обязательное поле' })}
            />
            {pwForm.formState.errors.confirmPassword && (
              <p className="text-xs text-red-500 mt-1">{pwForm.formState.errors.confirmPassword.message}</p>
            )}
          </div>
          <button
            type="submit"
            disabled={changePassword.isPending}
            className="w-full bg-brand-600 text-white py-2.5 rounded-xl text-sm font-medium disabled:opacity-60"
          >
            {changePassword.isPending ? 'Сохранение...' : 'Сменить пароль'}
          </button>
        </form>
      </section>

      {/* Telegram */}
      <section className="bg-white border border-gray-200 rounded-2xl p-5 mb-4">
        <h3 className="font-semibold text-gray-700 mb-1">Telegram уведомления</h3>
        <p className="text-sm text-gray-400 mb-4">
          Привяжите Telegram-аккаунт, чтобы получать уведомления о важных событиях.
        </p>

        {telegramStatus?.linked ? (
          <div className="space-y-3">
            <div className="bg-green-50 border border-green-200 rounded-xl p-3">
              <p className="text-sm font-medium text-green-700">✅ Telegram привязан</p>
              {telegramStatus.link?.username && (
                <p className="text-xs text-green-600 mt-1">@{telegramStatus.link.username}</p>
              )}
              <p className="text-xs text-gray-400 mt-1">
                Привязан: {telegramStatus.link?.linkedAt ? new Date(telegramStatus.link.linkedAt).toLocaleDateString('ru') : '—'}
              </p>
            </div>
            <button
              onClick={() => unlinkTelegram.mutate()}
              className="text-sm text-red-500 hover:text-red-700"
            >
              Отвязать Telegram
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-500">
              <p className="font-medium text-gray-700 mb-2">Как привязать:</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>Нажмите «Получить код»</li>
                <li>Скопируйте команду</li>
                <li>Откройте бота в Telegram и отправьте команду</li>
              </ol>
            </div>

            {linkCodeData?.code ? (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-sm text-gray-500 mb-1">Код (действует 10 минут):</p>
                <div className="flex items-center gap-2">
                  <code className="bg-white border border-gray-200 px-3 py-2 rounded-lg font-mono text-lg font-bold text-brand-700 flex-1 text-center">
                    {linkCodeData.code}
                  </code>
                  <button
                    onClick={() => copyCode(linkCodeData.code!)}
                    className="bg-brand-600 text-white px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap"
                  >
                    {copied ? '✅ Скопировано' : 'Скопировать'}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-2">Отправьте боту: <code>/start {linkCodeData.code}</code></p>
              </div>
            ) : (
              <button
                onClick={() => generateCode()}
                disabled={isPending}
                className="w-full bg-brand-600 text-white py-3 rounded-xl font-medium disabled:opacity-60"
              >
                {isPending ? 'Генерация...' : '📱 Получить код привязки'}
              </button>
            )}
          </div>
        )}
      </section>

      {/* Bot info */}
      <section className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
        <p className="text-sm text-amber-700 font-medium mb-1">🤖 Telegram бот</p>
        <p className="text-xs text-amber-600">
          После привязки вы можете задавать боту вопросы в свободной форме:
          «Сколько активных аренд?», «Свободные велики», «Просрочки».
          Также доступны команды: /today, /active, /point, /bike КОД
        </p>
      </section>

      {/* Tenant settings (owner only) */}
      {user?.role === 'owner' && settingsData && (
        <section className="bg-white border border-gray-200 rounded-2xl p-5 mb-4">
          <h3 className="font-semibold text-gray-700 mb-3">⚙️ Настройки аренды</h3>
          {settingsSaved && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-3 text-sm text-green-700 font-medium">
              ✅ Настройки сохранены
            </div>
          )}
          {updateSettings.isError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-3 text-sm text-red-700">
              {(updateSettings.error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? 'Ошибка сохранения'}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Льготный период (grace period)
              </label>
              <p className="text-xs text-gray-400 mb-2">Время после окончания аренды, которое не тарифицируется. Применяется к почасовой и суточной.</p>
              <div className="flex flex-wrap gap-2">
                {GRACE_OPTIONS.map((min) => (
                  <button
                    key={min}
                    onClick={() => updateSettings.mutate({ gracePeriodMinutes: min })}
                    disabled={updateSettings.isPending}
                    className={`px-4 py-2 rounded-xl text-sm font-medium border transition-colors disabled:opacity-60 ${
                      settingsData.gracePeriodMinutes === min
                        ? 'bg-brand-600 border-brand-600 text-white'
                        : 'border-gray-200 text-gray-600 hover:border-brand-300'
                    }`}
                  >
                    {min === 0 ? 'Нет' : `${min} мин`}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Часовой пояс</label>
              <select
                value={settingsData.timezone}
                onChange={(e) => updateSettings.mutate({ timezone: e.target.value })}
                disabled={updateSettings.isPending}
                className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
              >
                {COMMON_TIMEZONES.map((tz) => (
                  <option key={tz.value} value={tz.value}>{tz.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Контактный телефон (для QR-страницы)</label>
              <div className="flex gap-2">
                <input
                  type="tel"
                  defaultValue={settingsData.contactPhone ?? ''}
                  id="contact-phone-input"
                  placeholder="+7 911 000 0000"
                  className="flex-1 border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                />
                <button
                  onClick={() => {
                    const val = (document.getElementById('contact-phone-input') as HTMLInputElement)?.value
                    if (val !== undefined) updateSettings.mutate({ contactPhone: val })
                  }}
                  disabled={updateSettings.isPending}
                  className="bg-brand-600 text-white px-4 py-3 rounded-xl text-sm font-medium disabled:opacity-60"
                >
                  Сохранить
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">Отображается на публичной странице велосипеда (при сканировании клиентом)</p>
            </div>

            <div className="pt-2 border-t border-gray-100">
              <label className="block text-sm font-medium text-gray-700 mb-1">🔔 Уведомления «Требует внимания»</label>
              <p className="text-xs text-gray-400 mb-3">Помечать аренду как требующую внимания, если её открыли/закрыли не сканируя QR.</p>
              {([
                ['notifyNonQrOpen', 'Открытие не по QR'],
                ['notifyNonQrClose', 'Закрытие не по QR'],
              ] as const).map(([key, label]) => {
                const checked = Boolean(settingsData[key])
                return (
                  <label key={key} className="flex items-center justify-between py-2 cursor-pointer">
                    <span className="text-sm text-gray-700">{label}</span>
                    <button
                      type="button"
                      onClick={() => updateSettings.mutate({ [key]: !checked })}
                      disabled={updateSettings.isPending}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-60 ${checked ? 'bg-brand-600' : 'bg-gray-300'}`}
                      aria-pressed={checked}
                    >
                      <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-5' : 'translate-x-1'}`} />
                    </button>
                  </label>
                )
              })}
            </div>
          </div>
        </section>
      )}
    </div>
  )
}
